import { FNO_UNIVERSE, NSE_EXTRA_STOCKS, NSE_INDICES, type Analysed, type IndexDef } from '@planb/core';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';

export const indexById = (id: string | null | undefined): IndexDef | undefined => NSE_INDICES.find((i) => i.id === id);
export const dateSeed = () => Number(new Date().toISOString().slice(0, 10).replace(/-/g, ''));
export const ALL_SYMBOLS = [...new Set([...FNO_UNIVERSE.map((r) => r[0]), ...NSE_EXTRA_STOCKS.map((r) => r[0])])].sort();
const BROAD = new Set(['FNO', 'NIFTY50', 'NIFTYNEXT50', 'NIFTY100', 'NIFTY500', 'MIDCAP150', 'SMALLCAP100']);

/** Query string that carries the chosen index (and a non-default simulated session) between pages. */
export function carry(opts: { index?: string | null; live: boolean; seed: number }) {
  const q = new URLSearchParams();
  if (opts.index && opts.index !== 'FNO') q.set('index', opts.index);
  if (!opts.live && opts.seed !== dateSeed()) q.set('seed', String(opts.seed));
  return q.size ? `?${q}` : '';
}

export const stockPath = (sym: string, query: string) => `/stock/${encodeURIComponent(sym)}${query}`;

export function IndexSelect({ value, onChange, className }: { value: string; onChange: (id: string) => void; className?: string }) {
  const group = (label: string, list: IndexDef[]) => (
    <SelectGroup>
      <SelectLabel>{label}</SelectLabel>
      {list.map((i) => (
        <SelectItem key={i.id} value={i.id}>
          {i.name} <span className="text-muted-foreground">({i.members.length})</span>
        </SelectItem>
      ))}
    </SelectGroup>
  );
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label="Index" className={className ?? 'h-9 min-w-44 rounded-full bg-card font-medium'}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper" className="max-h-96">
        {group('Broad', NSE_INDICES.filter((i) => BROAD.has(i.id)))}
        {group('Sector', NSE_INDICES.filter((i) => !BROAD.has(i.id)))}
      </SelectContent>
    </Select>
  );
}

/** Plain-English read of a stock's numbers. */
export function story(s: Analysed) {
  const who = s.bull ? 'Buyers' : 'Sellers';
  const vol = s.relVol >= 1.3 ? `on ${s.relVol.toFixed(1)}× its normal volume — big participants are active`
    : s.relVol >= 0.9 ? 'on about normal volume' : 'on light volume — conviction is weak';
  const oi = {
    'Long build-up': 'Traders are opening fresh long positions (price ↑, open interest ↑).',
    'Short covering': 'The rise is partly sellers exiting their shorts (price ↑, open interest ↓) — can fade.',
    'Short build-up': 'Traders are opening fresh short positions (price ↓, open interest ↑).',
    'Long unwinding': 'The fall is partly buyers exiting (price ↓, open interest ↓) — can stabilise.',
    'No F&O data': 'This stock has no futures contract, so there is no open-interest signal.',
  }[s.buildUp];
  return `${who} are in control: ${s.bull ? 'up' : 'down'} ${Math.abs(s.pct).toFixed(2)}% ${vol}. ${oi}`;
}

/** Ends the broker session and deletes the stored Kite token; the app falls back to simulated data. */
export async function disconnectKite() {
  await api('/v1/kite/logout', { method: 'POST' }).catch(() => null);
  location.reload();
}

export async function signOut() {
  await api('/v1/auth/logout', { method: 'POST' }).catch(() => null);
  location.assign('/login');
}

